import React, { useEffect, useState } from "react";
import ProductList from "src/components/ProductList";
import { SectionSettings, Product } from "../../interfaces/sanity";
import { StyledWrap, StyledHeading, StyledButton } from "./styles";
import { useAddItemToCart } from "src/context/siteContext";
import { encode } from "shopify-gid";

async function shopifyFetch({ query, variables }: { query: string; variables?: any }) {
  const endpoint = `https://${process.env.GATSBY_SHOPIFY_STORE}/api/2023-10/graphql.json`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || "",
    },
    body: JSON.stringify({ query, variables }),
  });
  return response.json();
}

const GET_FIRST_VARIANT_QUERY = `
  query getProductVariant($id: ID!) {
    product(id: $id) {
      variants(first: 1) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

interface Props {
  data: {
    buttonText: string;
    products: Product[];
    title: string;
    sectionSettings: SectionSettings;
  };
}

const BoughtTogetherSection = ({ data }: Props) => {
  const [ready, setReady] = useState(false);
  const [idList, setIdList] = useState([]);
  const { addItemsToCart } = useAddItemToCart();

  const getIds = (products) => products.map((product) => product._id);

  useEffect(() => {
    if (!data?.products) return;

    const idList = getIds(data?.products);

    const shopifyIds = idList.map((id) => {
      const numericId = id.replace("products-", "");
      const shopifyId = encode("Product", numericId, {
        accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
      });

      return shopifyId;
    });

    getData(shopifyIds);

    setReady(true);
  }, []);

  const getData = async (shopifyIds: string[]) => {
    const list = await Promise.all(
      shopifyIds.map(async (id) => {
        try {
          const { data } = await shopifyFetch({
            query: GET_FIRST_VARIANT_QUERY,
            variables: { id }
          });

          const variantNode = data?.product?.variants?.edges?.[0]?.node;
          
          if (!variantNode) return null;

          return variantNode.id as string;
        } catch (e) {
          console.error("Error fetching product variant for bought together", e);
          return null;
        }
      })
    );

    setIdList(list.filter(Boolean));
  };

  const addProducts = async () => {
    const structArr = idList.map((id) => {
      return {
        variantId: id,
        quantity: 1,
        attributes: [],
      };
    });

    addItemsToCart(structArr);
  };

  return (
    <StyledWrap>
      <StyledHeading>{data.title}</StyledHeading>
      <ProductList
        products={data.products}
        mobileGrid={true}
        addToCart={true}
      />
      {data.buttonText && ready && (
        <StyledButton onClick={addProducts}>{data.buttonText}</StyledButton>
      )}
    </StyledWrap>
  );
};

export default BoughtTogetherSection;
